/**
 * An open project: the question, the cohort, the protocol, and what has
 * happened to whom.
 *
 * The ordering is the one a supervisor's questions arrive in — what are we
 * asking, how many are in, what are we doing to them, where have we got to,
 * where did the results go — and every number on it is observed rather than
 * kept: progress is derived from the runs, and a folder that has been moved
 * says so instead of being counted.
 */

import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  FolderOpen,
  Play,
  Plus,
  XCircle,
} from "lucide-react";

import { Button, Input } from "@/components/ui/primitives";
import { addProjectNote, revealPath } from "@/ipc/api";
import type { PipelineKind, Project, ProjectRun, SubjectProgress } from "@/ipc/contract";
import { cn, formatDateTime, formatDuration } from "@/lib/utils";
import { goToTab } from "@/pipeline/actions";
import { usePipelineSession } from "@/pipeline/session";
import { Eyebrow, SURFACE } from "@/nav/home.design";
import { useProjects } from "./store";

const KIND_LABEL: Record<PipelineKind, string> = {
  dwi: "DWI",
  pwi: "PWI",
  combined: "DWI + PWI",
};

const PROGRESS: Record<
  SubjectProgress,
  { label: string; icon: typeof Circle; className: string }
> = {
  done: { label: "Done", icon: CheckCircle2, className: "text-emerald-600" },
  doneOffProtocol: {
    label: "Done, different protocol",
    icon: AlertTriangle,
    className: "text-amber-600",
  },
  failed: { label: "Failed", icon: XCircle, className: "text-red-600" },
  cancelled: { label: "Stopped", icon: Circle, className: "text-neutral-400" },
  notStarted: { label: "Not started", icon: Circle, className: "text-neutral-300" },
};

/** Mirrors `projects::progress_of`, so the page and the record agree. */
function progressOf(project: Project, subjectId: string): SubjectProgress {
  const runs = project.runs.filter((r) => r.subjectId === subjectId);
  if (runs.length === 0) return "notStarted";
  const latest = runs.reduce((a, b) => (a.startedIso > b.startedIso ? a : b));
  if (latest.state === "failed") return "failed";
  if (latest.state === "cancelled") return "cancelled";
  const norm = (v: string[]) => [...new Set(v)].sort().join(",");
  const same =
    latest.pipelineKind === project.protocol.pipelineKind &&
    norm(latest.stages.dwi) === norm(project.protocol.stages.dwi) &&
    norm(latest.stages.pwi) === norm(project.protocol.stages.pwi);
  return same ? "done" : "doneOffProtocol";
}

function Stat({ value, label, tone }: { value: string | number; label: string; tone?: string }) {
  return (
    <div className="flex flex-col">
      <span className={cn("text-[17px] font-semibold tabular-nums leading-none", tone ?? "text-neutral-900")}>
        {value}
      </span>
      <span className="mt-1.5 text-[10px] uppercase tracking-[0.1em] text-neutral-400">{label}</span>
    </div>
  );
}

function latestRun(project: Project, subjectId: string): ProjectRun | null {
  const runs = project.runs.filter((r) => r.subjectId === subjectId);
  if (runs.length === 0) return null;
  return runs.reduce((a, b) => (a.startedIso > b.startedIso ? a : b));
}

export function ProjectPage({ project }: { project: Project }) {
  const setOpen = useProjects((s) => s.setOpen);
  const refresh = useProjects((s) => s.refresh);
  const [note, setNote] = useState("");
  const [filter, setFilter] = useState<SubjectProgress | "all">("all");

  const rows = useMemo(
    () =>
      project.cohort.included.map((id) => ({
        subjectId: id,
        progress: progressOf(project, id),
        run: latestRun(project, id),
      })),
    [project],
  );
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    for (const r of rows) c[r.progress] = (c[r.progress] ?? 0) + 1;
    return c;
  }, [rows]);
  const shown = filter === "all" ? rows : rows.filter((r) => r.progress === filter);

  useEffect(() => {
    void refresh();
  }, [project.modifiedIso, refresh]);

  const addNote = async () => {
    const text = note.trim();
    if (!text) return;
    setNote("");
    try {
      setOpen(await addProjectNote(project.id, text));
    } catch {
      /* the field keeps the text on failure via the caller's retry */
    }
  };

  /**
   * Run the subjects that are not done, under this project's protocol.
   *
   * It runs rather than opening a form with the answers already in it: the
   * protocol was fixed when the project was made and the cohort was decided
   * then too, so there is nothing left to confirm — and a button that says
   * "run" and does not is the kind of small lie this interface has been
   * collecting. The Pipeline tab is where it lands, because that is where the
   * stage table is.
   */
  const runRemaining = async () => {
    const session = usePipelineSession.getState();
    session.startNewAnalysis(project.protocol.pipelineKind, "full");
    await session.chooseDatasetRoot(project.cohort.root);
    const live = usePipelineSession.getState();
    live.selectAllMatched(rows.filter((r) => r.progress !== "done").map((r) => r.subjectId));
    live.patchDraft({
      pipelineKind: project.protocol.pipelineKind,
      stages: project.protocol.stages,
      outputRoot: project.outputRoot,
    });
    goToTab("pipeline");
    await usePipelineSession.getState().run(project.protocol.stages);
  };

  const remaining = rows.filter((r) => r.progress !== "done").length;

  return (
    <div className="h-full overflow-auto bg-ads-bg">
      <div className="mx-auto flex max-w-5xl flex-col gap-7 px-9 py-7">
        <header>
          <button
            type="button"
            onClick={() => setOpen(null)}
            className="text-[11px] text-ads-accent hover:underline"
          >
            ← All projects
          </button>
          <h1 className="mt-2 text-[21px] font-semibold tracking-[-0.01em] text-neutral-900">
            {project.name}
          </h1>
          {project.question ? (
            // Verbatim, in quotes: the app never rewrites the researcher's
            // sentence, and it is the thing worth re-reading every time.
            <p className="mt-2 max-w-3xl border-l-2 border-ads-accent-border pl-3 text-[13px] italic leading-relaxed text-neutral-700">
              “{project.question}”
            </p>
          ) : (
            <p className="mt-2 text-[12px] text-neutral-400">
              No question recorded. Add one in the log below if you want it at the top.
            </p>
          )}
        </header>

        <div className={cn(SURFACE, "flex flex-wrap items-end gap-x-10 gap-y-4 px-6 py-5")}>
          <Stat value={project.cohort.included.length} label="In the cohort" />
          <Stat value={counts.done ?? 0} label="Done" tone="text-emerald-700" />
          {counts.doneOffProtocol ? (
            <Stat value={counts.doneOffProtocol} label="Off protocol" tone="text-amber-700" />
          ) : null}
          {counts.failed ? <Stat value={counts.failed} label="Failed" tone="text-red-700" /> : null}
          <Stat value={counts.notStarted ?? 0} label="Not started" />
          <Stat value={project.cohort.excluded.length} label="Excluded" />
          <span className="flex-1" />
          <Button variant="primary" disabled={remaining === 0} onClick={() => void runRemaining()}>
            <Play className="h-3.5 w-3.5" />
            Run the {remaining} not done
          </Button>
        </div>

        {/* ------------------------------------------------- protocol --- */}
        <section className={cn(SURFACE, "px-6 py-5")}>
          <Eyebrow>Protocol — what every subject receives</Eyebrow>
          <div className="mt-2.5 flex flex-wrap items-baseline gap-x-6 gap-y-1.5 text-[12px] text-neutral-700">
            <span className="font-semibold text-neutral-900">
              {KIND_LABEL[project.protocol.pipelineKind]}
            </span>
            <span className="tabular-nums">
              {project.protocol.stages.dwi.length + project.protocol.stages.pwi.length} stages
            </span>
            <span className="text-neutral-400">Fixed {formatDateTime(project.protocol.fixedIso)}</span>
          </div>
          <div className="mt-2 font-mono text-[10.5px] leading-relaxed text-neutral-500">
            {project.protocol.stages.dwi.length ? (
              <div>DWI · {project.protocol.stages.dwi.join(" → ")}</div>
            ) : null}
            {project.protocol.stages.pwi.length ? (
              <div>PWI · {project.protocol.stages.pwi.join(" → ")}</div>
            ) : null}
          </div>
          {counts.doneOffProtocol ? (
            <p className="mt-3 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] leading-relaxed text-amber-900">
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
              <span>
                {counts.doneOffProtocol} subject{counts.doneOffProtocol === 1 ? " was" : "s were"}{" "}
                processed with different stages. They finished, but the cohort is not uniform until
                they are re-run.
              </span>
            </p>
          ) : null}
        </section>

        {/* --------------------------------------------------- cohort --- */}
        <section>
          <div className="mb-2.5 flex flex-wrap items-center gap-2">
            <Eyebrow className="mr-1">Subjects</Eyebrow>
            {(["all", "notStarted", "done", "doneOffProtocol", "failed"] as const)
              .filter((f) => f === "all" || counts[f])
              .map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
                    filter === f
                      ? "border-ads-accent-border bg-ads-accent-soft text-ads-accent"
                      : "border-ads-border bg-ads-surface text-neutral-600 hover:border-ads-border-strong",
                  )}
                >
                  {f === "all" ? "All" : PROGRESS[f].label}
                  <span className="ml-1 tabular-nums text-neutral-400">{counts[f] ?? 0}</span>
                </button>
              ))}
            <span className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              onClick={() => void revealPath(project.outputRoot).catch(() => undefined)}
            >
              <FolderOpen className="h-3.5 w-3.5" />
              Output folder
            </Button>
          </div>

          <div className={cn(SURFACE, "overflow-hidden")}>
            {shown.map(({ subjectId, progress, run }) => {
              const p = PROGRESS[progress];
              const Icon = p.icon;
              return (
                <div
                  key={subjectId}
                  className="flex items-center gap-3 border-b border-ads-border px-4 py-2 last:border-0"
                >
                  <Icon className={cn("h-3.5 w-3.5 shrink-0", p.className)} />
                  <span className="w-44 shrink-0 truncate text-[12.5px] font-medium text-neutral-800">
                    {subjectId}
                  </span>
                  <span className="w-40 shrink-0 text-[11px] text-neutral-500">{p.label}</span>
                  <span className="min-w-0 flex-1 truncate text-[11px] tabular-nums text-neutral-400">
                    {run
                      ? [
                          formatDateTime(run.startedIso),
                          run.seconds != null ? formatDuration(run.seconds) : null,
                          run.deviceLabel || null,
                          run.error,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : ""}
                  </span>
                </div>
              );
            })}
            {shown.length === 0 ? (
              <p className="px-4 py-5 text-center text-[11.5px] text-neutral-500">
                No subject in this state.
              </p>
            ) : null}
          </div>
        </section>

        {/* ------------------------------------------------ excluded --- */}
        {project.cohort.excluded.length ? (
          <section>
            <Eyebrow className="mb-2.5">Excluded, and why</Eyebrow>
            <div className={cn(SURFACE, "overflow-hidden")}>
              {project.cohort.excluded.map((e) => (
                <div
                  key={e.subjectId}
                  className="flex items-start gap-3 border-b border-ads-border px-4 py-2 last:border-0"
                >
                  <span className="w-44 shrink-0 truncate text-[12.5px] font-medium text-neutral-500">
                    {e.subjectId}
                  </span>
                  <span className="min-w-0 flex-1 text-[11.5px] leading-relaxed text-neutral-700">
                    {e.reason}
                    {/* Whose finding it is matters: one is evidence, the other
                        is judgement, and a methods section needs both apart. */}
                    <span className="ml-1.5 text-[10px] uppercase tracking-wide text-neutral-400">
                      {e.fromScan ? "from the scan" : "reviewer"}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* ------------------------------------------------------ log --- */}
        <section>
          <Eyebrow className="mb-2.5">Log</Eyebrow>
          <div className="mb-2 flex gap-1.5">
            <Input
              value={note}
              placeholder="Record a decision — why something was excluded, what changed, what you noticed"
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void addNote();
              }}
            />
            <Button variant="outline" onClick={() => void addNote()} disabled={!note.trim()}>
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          </div>
          <div className={cn(SURFACE, "overflow-hidden")}>
            {[...project.log].reverse().map((entry, i) => (
              <div
                key={`${entry.iso}-${i}`}
                className="flex items-baseline gap-3 border-b border-ads-border px-4 py-1.5 last:border-0"
              >
                <span className="w-36 shrink-0 text-[10.5px] tabular-nums text-neutral-400">
                  {formatDateTime(entry.iso)}
                </span>
                <span className="min-w-0 flex-1 text-[11.5px] leading-relaxed text-neutral-700">
                  {entry.text}
                </span>
              </div>
            ))}
            {project.log.length === 0 ? (
              <p className="px-4 py-4 text-center text-[11.5px] text-neutral-500">Nothing yet.</p>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
